import React from 'react';
import './PortfolioQualityScore.css';
import { convictionStep } from '../../utils/convictionLabel';

// The one label scale (utils/convictionLabel.js). Was A-F letters at
// 80/65/50/35, a sixth scale: a 74 read "B" here and "Promising" everywhere else.
const getGrade = (score) => {
  const st = convictionStep(score);
  return { label: st.label, color: st.color };
};

function regimeLabel(r) {
  const s = (r || '').toString().toLowerCase();
  if (s.includes('risk_on') || s.includes('risk-on') || s.includes('riskon')) return 'Risk-On';
  if (s.includes('risk_off') || s.includes('risk-off') || s.includes('riskoff')) return 'Risk-Off';
  if (!r) return null;
  return 'Neutral';
}

export default function PortfolioQualityScore({ items, scores, analysisResult }) {
  if (!items?.length || !scores?.length) return null;

  const totalValue = scores.reduce((sum, s) => sum + (s.position_value || 0), 0);
  if (totalValue <= 0) return null;

  // ── Positioning (macro-aware): the existing blended conviction, value-weighted.
  // This is "how should I be positioned in TODAY'S market" — it moves with the regime.
  const localPositioning = scores.reduce((sum, s) => {
    const w = (s.position_value || 0) / totalValue;
    return sum + (s.combined_conviction || 0) * w;
  }, 0);

  // ── Holdings (macro-NEUTRAL): value-weighted QUALITY only. It also averaged
  // Setup (momentum, which swings with the market) and Convex, so the "stable"
  // number wasn't. Mirrors backend/services/portfolio_grade.py; the server's
  // numbers win when present (analysisResult.portfolio_grade).
  let hqWeight = 0;
  let hqSum = 0;
  for (const s of scores) {
    if (s.stock_quality_score == null) continue;
    const w = s.position_value || 0;
    hqSum += s.stock_quality_score * w;
    hqWeight += w;
  }
  const server = analysisResult?.portfolio_grade;
  const holdingsScore = server?.holdings?.score
    ?? (hqWeight > 0 ? hqSum / hqWeight : localPositioning);

  const positioningScore = server?.positioning?.score ?? localPositioning;
  const hq = getGrade(holdingsScore);
  const pos = getGrade(positioningScore);

  const regime = regimeLabel(analysisResult?.regime);
  const turbLabel = analysisResult?.turbulence?.turbulence_label;
  const dampened =
    analysisResult?.turbulence && typeof analysisResult.turbulence.dampener === 'number'
      ? analysisResult.turbulence.dampener < 1.0
      : false;
  const softMarket = (regime && regime !== 'Risk-On') || dampened;
  const gap = holdingsScore - positioningScore;

  // Describes the data; never tells anyone to buy, sell or rotate (the old
  // copy said "stay defensive and keep dry powder").
  let takeaway = server?.takeaway;
  if (!takeaway) {
    const market = `today's ${regime || 'market'}${regime ? ' market' : ''}${turbLabel && dampened ? ` (${turbLabel.toLowerCase()})` : ''}`;
    if (holdingsScore < 57) {
      takeaway = `Your holdings score ${hq.label} (${holdingsScore.toFixed(0)}) on business quality alone, so the companies themselves are the weaker part of the picture, whatever the market does.`;
    } else if (gap >= 8 && softMarket) {
      takeaway = `Your holdings score ${hq.label} (${holdingsScore.toFixed(0)}) on business quality. Positioning reads ${pos.label} (${positioningScore.toFixed(0)}) because of ${market}, not the companies you own.`;
    } else {
      takeaway = `Holdings (${hq.label}, ${holdingsScore.toFixed(0)}) and Positioning (${pos.label}, ${positioningScore.toFixed(0)}) tell the same story: what you own and how it fits ${market} line up.`;
    }
  }

  return (
    <div className="portfolio-quality-score">
      <div className="pqs-header">
        <h3>Portfolio Grade</h3>
      </div>

      <div className="pqs-dual">
        <div className="pqs-block">
          <div className="pqs-grade" style={{ borderColor: hq.color, color: hq.color }}>
            {Math.round(holdingsScore)}
          </div>
          <div className="pqs-block-body">
            <div className="pqs-number" style={{ color: hq.color }}>{hq.label}</div>
            <div className="pqs-label">Holdings</div>
            <div className="pqs-breakdown">Business quality of what you own, whatever the market does</div>
          </div>
        </div>

        <div className="pqs-divider" aria-hidden="true" />

        <div className="pqs-block">
          <div className="pqs-grade" style={{ borderColor: pos.color, color: pos.color }}>
            {Math.round(positioningScore)}
          </div>
          <div className="pqs-block-body">
            <div className="pqs-number" style={{ color: pos.color }}>{pos.label}</div>
            <div className="pqs-label">Positioning</div>
            <div className="pqs-breakdown">
              Your holdings in today's {regime || 'current'} market{turbLabel ? `, ${turbLabel}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="pqs-takeaway">{takeaway}</div>
    </div>
  );
}
